import { useState, useEffect } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { ENV } from "@/config/env";
import {
  LayoutDashboard,
  Users,
  User,
  FileText,
  LayoutTemplate,
  ChevronLeft,
  ChevronDown,
  Database,
  GraduationCap,
  Building2,
  Building,
  Layers,
  Wrench,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useCurrentUser } from "@/pages/auth/hooks/useAuth";
import { ROUTES } from "@/utils/routes";

interface SubMenuItem {
  name: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
}

interface MenuItem {
  name: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
  badge?: string;
  superuserOnly?: boolean;
  children?: SubMenuItem[];
}

const MASTER_ROUTES = [
  ROUTES.DEGREE,
  ROUTES.INDUSTRY,
  ROUTES.TEAM,
  ROUTES.DEPARTMENT,
  ROUTES.CATEGORY,
  ROUTES.SKILL,
  ROUTES.MASTER_MANAGEMENT,
];

const MENU_ITEMS: MenuItem[] = [
  {
    name: "Dashboard",
    icon: LayoutDashboard,
    path: ROUTES.DASHBOARD,
    superuserOnly: true,
  },
  {
    name: "Candidates",
    icon: Users,
    path: ROUTES.CANDIDATES,
    superuserOnly: true,
  },
  {
    name: "Master",
    icon: Database,
    path: ROUTES.DEGREE,
    superuserOnly: true,
    children: [
      { name: "Degree", icon: GraduationCap, path: ROUTES.DEGREE },
      { name: "Industry", icon: Building2, path: ROUTES.INDUSTRY },
      { name: "Team", icon: Users, path: ROUTES.TEAM },
      { name: "Department", icon: Building, path: ROUTES.DEPARTMENT },
      { name: "Category", icon: Layers, path: ROUTES.CATEGORY },
      { name: "Skill", icon: Wrench, path: ROUTES.SKILL },
    ],
  },
  { name: "My Profile", icon: User, path: ROUTES.MY_PROFILE },
  { name: "My Resumes", icon: FileText, path: ROUTES.RESUMES },
  { name: "Templates", icon: LayoutTemplate, path: ROUTES.TEMPLATES },
];

interface SidebarProps {
  mobileMenuOpen?: boolean;
  setMobileMenuOpen?: (open: boolean) => void;
}

export function Sidebar({ mobileMenuOpen, setMobileMenuOpen }: SidebarProps) {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { data: user } = useCurrentUser();

  const isSuperuser = Boolean(user?.is_superuser);

  const visibleMenuItems = MENU_ITEMS.filter(
    (item) => !item.superuserOnly || isSuperuser,
  );

  const isMasterActive = MASTER_ROUTES.some(
    (p) => location.pathname === p || location.pathname.startsWith(p),
  );

  // Track expanded parent items
  const [openParents, setOpenParents] = useState<Record<string, boolean>>({
    Master: isMasterActive,
  });

  useEffect(() => {
    if (isMasterActive) {
      setOpenParents((prev) => ({ ...prev, Master: true }));
    }
  }, [isMasterActive]);

  const toggleParent = (name: string) => {
    setOpenParents((prev) => ({
      ...prev,
      [name]: !prev[name],
    }));
  };

  const sidebarContent = (
    <div className="h-full flex flex-col">
      {/* Brand Logo Header */}
      <div className="h-16 flex items-center px-4 justify-between border-b border-slate-800 bg-slate-950/50 shrink-0">
        <div className="flex items-center gap-3 overflow-hidden">
          <img
            src={ENV.COMPANY_LOGO}
            alt={ENV.COMPANY_NAME}
            className="h-8 object-contain shrink-0"
          />
        </div>
        {setMobileMenuOpen && (
          <Button
            size="icon"
            variant="ghost"
            onClick={() => setMobileMenuOpen(false)}
            className="md:hidden text-slate-400 hover:text-white h-8 w-8"
          >
            <X className="h-5 w-5" />
          </Button>
        )}
      </div>

      {/* Menu List */}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto pr-2 custom-scrollbar">
        {visibleMenuItems.map((item, i) => {
          const hasChildren = item.children && item.children.length > 0;
          const isParentActive =
            hasChildren &&
            (item.name === "Master"
              ? isMasterActive
              : location.pathname.startsWith(item.path));
          const isOpen = openParents[item.name];

          if (hasChildren) {
            return (
              <div key={i} className="space-y-1">
                {/* Parent Menu Item */}
                <button
                  type="button"
                  onClick={() => {
                    if (collapsed) {
                      setCollapsed(false);
                      setOpenParents((prev) => ({
                        ...prev,
                        [item.name]: true,
                      }));
                      navigate(item.children![0].path);
                    } else {
                      toggleParent(item.name);
                    }
                  }}
                  className={cn(
                    "w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 group relative text-sm text-left cursor-pointer outline-none focus:outline-none",
                    isParentActive && !isOpen
                      ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white font-medium shadow-[0_0_15px_rgba(147,51,234,0.3)]"
                      : isParentActive
                        ? "bg-white/10 text-white font-medium"
                        : "text-slate-400 hover:text-white hover:bg-white/5",
                  )}
                  title={collapsed ? item.name : undefined}
                >
                  <item.icon
                    className={cn(
                      "w-5 h-5 shrink-0",
                      collapsed ? "mx-auto" : "",
                    )}
                  />
                  {!collapsed && (
                    <>
                      <span className="flex-1 text-sm truncate">{item.name}</span>
                      <ChevronDown
                        className={cn(
                          "h-4 w-4 transition-transform duration-200 shrink-0",
                          isParentActive ? "text-white" : "text-slate-400",
                          isOpen ? "rotate-180" : "",
                        )}
                      />
                    </>
                  )}
                </button>

                {/* Children Sub-Menu */}
                {!collapsed && (
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                        className="pl-3 space-y-1 overflow-hidden"
                      >
                        {item.children!.map((child, childIdx) => (
                          <NavLink
                            key={childIdx}
                            to={child.path}
                            onClick={() =>
                              setMobileMenuOpen && setMobileMenuOpen(false)
                            }
                            className={({ isActive }) =>
                              cn(
                                "flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200 group relative text-xs outline-none focus:outline-none",
                                isActive
                                  ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white font-medium shadow-[0_0_15px_rgba(147,51,234,0.3)]"
                                  : "text-slate-400 hover:text-white hover:bg-white/5",
                              )
                            }
                          >
                            <child.icon className="w-4 h-4 shrink-0" />
                            <span className="truncate flex-1">{child.name}</span>
                          </NavLink>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                )}
              </div>
            );
          }

          return (
            <NavLink
              key={i}
              to={item.path}
              onClick={() => setMobileMenuOpen && setMobileMenuOpen(false)}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 group relative text-sm outline-none focus:outline-none",
                  isActive
                    ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white font-medium shadow-[0_0_15px_rgba(147,51,234,0.3)]"
                    : "text-slate-400 hover:text-white hover:bg-white/5",
                )
              }
              title={collapsed ? item.name : undefined}
            >
              <item.icon
                className={cn(
                  "w-5 h-5 shrink-0",
                  collapsed ? "mx-auto" : "",
                )}
              />
              {!collapsed && (
                <span className="flex-1 text-sm truncate">{item.name}</span>
              )}
              {!collapsed && item.badge && (
                <span className="ml-auto bg-indigo-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0">
                  {item.badge}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>
    </div>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <motion.aside
        initial={false}
        animate={{ width: collapsed ? 80 : 280 }}
        className="h-screen bg-slate-900 text-slate-300 flex flex-col border-r border-slate-800 transition-all duration-300 sticky top-0 flex-shrink-0 z-40 hidden md:flex"
      >
        {sidebarContent}

        {/* Collapse Toggle */}
        <Button
          size="icon"
          onClick={() => setCollapsed(!collapsed)}
          className="absolute -right-3 top-20 bg-slate-800 border border-slate-700 text-white h-7 w-7 rounded-full hover:bg-slate-700 z-50 shadow-md p-0"
        >
          <ChevronLeft
            className={cn(
              "h-4 w-4 transition-transform",
              collapsed && "rotate-180",
            )}
          />
        </Button>
      </motion.aside>

      {/* Mobile Drawer Sidebar */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 md:hidden flex">
          <div
            className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm"
            onClick={() => setMobileMenuOpen && setMobileMenuOpen(false)}
          />
          <div className="relative w-72 max-w-[80vw] bg-slate-900 h-full z-10 shadow-2xl border-r border-slate-800">
            {sidebarContent}
          </div>
        </div>
      )}
    </>
  );
}
